import React from "react";
import ChangeArrow from "./ChangeArrow";
import I18N from "../I18N";
import { T } from "../traks";
import { Row } from "reactstrap";

export const PortfolioInfoBox = ({
	title,
	items = [],
	infoText,
}) => {
	return (
		<div className="ad_portfolio_infobox">
			<div className="ad_portfolio_infobox_header">
				{title}
			</div>
			<div className="ad_portfolio_infobox_body">
				{items.map((item, idx) => (
					<div className="ad_portfolio_infobox_row" key={idx}>
						<div className="ad_portfolio_infobox_label">
							{item.label}
						</div>
						<div className="ad_portfolio_infobox_value">
							{item.value}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export const PortfolioSummaryGrid = ({ summary, currencyFallback }) => {
const currencyTicker = summary && summary.totals && summary.totals.bankrollTicker ? summary.totals.bankrollTicker : currencyFallback;
const placeholder = <span>{I18N.floatval_unit(0, 2, currencyTicker)}</span>;

const shortingEnabled = summary && summary.settings && summary.settings.shortingEnabled === true;
const diversificationEnabled = summary && summary.settings && summary.settings.diversificationEnabled === true;
const betaEnabled = summary && summary.settings && summary.settings.betaEnabled === true;

 let totalChangeValue = summary
 	? (
 		<span className={`ad_portfolio_infobox_compound ${summary.totals.totalChangeAbsolute > 0 ? "ad_growth_positive" : (summary.totals.totalChangeAbsolute < 0 ? "ad_growth_negative" : "ad_growth_zero")}`}>
			<span className="ad_portfolio_infobox_value_primary">
				{I18N.floatval_unit(summary.totals.totalChangeAbsolute, 2, currencyTicker)}
			</span>
 			<span className="ad_portfolio_infobox_value_secondary">
 				<ChangeArrow pct={summary.totals.totalChangePercent} suffix="%" show_arrow={false} />
 			</span>
 		</span>
 	) : placeholder;


	const bankAccountValue = summary ? 
	(
		<span className="ad_portfolio_infobox_compound ad_growth_positive">
			{I18N.floatval_unit(summary.totals.bankrollValue, 2, currencyTicker)}
		</span>
	) : placeholder;

    let overviewItems = [
        { label: <T>Bank Account (cash)</T>, value: placeholder },
        { label: <T>Invested Value</T>, value: placeholder },
        { label: <T>Total portfolio value</T>, value: placeholder },
        { label: <T>Total Change</T>, value: placeholder },
    ];

    if (summary) {
	    overviewItems = [
            {
                label: <T>Bank Account (cash)</T>,
                value: bankAccountValue,
            },
            {
                label: <T>Invested Value</T>,
                value: I18N.floatval_unit(summary.totals.investedValue, 2, currencyTicker),
            },
            {
                label: <T>Total portfolio value</T>,
                value: I18N.floatval_unit(summary.totals.totalValue, 2, currencyTicker),
            },
            {
                label: <T>Portfolio return</T>,
                value: totalChangeValue,
            },
        ];
    }

if (shortingEnabled) {
    const netProfit = summary.totals.longProfit + summary.totals.shortProfit;

    overviewItems = [
        overviewItems[0],
        {
            label: <T>Shorting capacity</T>,
            value: summary.totals.shortCap ? I18N.floatval_unit(summary.totals.shortCap, 2, currencyTicker) : placeholder,
        },
        {
            label: <T>Long Market Value</T>,
            value: summary.totals.investedValue ? I18N.floatval_unit(summary.totals.investedValue, 2, currencyTicker) : placeholder,
        },
        {
            label: <T>Short Market Value</T>,
            value: summary.totals.shortExposure ? I18N.floatval_unit(summary.totals.shortExposure, 2, currencyTicker) : placeholder,
        },
        {
            label: <T>Net Portfolio Value</T>,
            value: summary.totals.portfolioNettoValue ? I18N.floatval_unit(summary.totals.portfolioNettoValue, 2, currencyTicker) : placeholder,
        },
        {
            label: <T>Profit/Loss on Short Positions</T>,
            value: (
                <span className={`ad_portfolio_infobox_compound ${summary.totals.shortProfit > 0 ? "ad_growth_positive" : (summary.totals.shortProfit < 0 ? "ad_growth_negative" : "ad_growth_zero")}`}>
                    <span className="ad_portfolio_infobox_value_primary">
                        {summary.totals.shortProfit ? I18N.floatval_unit(summary.totals.shortProfit, 2, currencyTicker) : placeholder}
                    </span>
                </span>
            ),
	    },
        {
            label: <T>Profit/Loss on Long Positions</T>,
            value: (
                <span className={`ad_portfolio_infobox_compound ${summary.totals.longProfit > 0 ? "ad_growth_positive" : (summary.totals.longProfit < 0 ? "ad_growth_negative" : "ad_growth_zero")}`}>
                    <span className="ad_portfolio_infobox_value_primary">
                        {summary.totals.longProfit ? I18N.floatval_unit(summary.totals.longProfit, 2, currencyTicker) : placeholder}
                    </span>
                </span>
            ),
        },
        {
            label: <T>Net Portfolio Return</T>,
            value: (
                <span className={`ad_portfolio_infobox_compound ${netProfit > 0 ? "ad_growth_positive" : (netProfit < 0 ? "ad_growth_negative" : "ad_growth_zero")}`}>
                    <span className="ad_portfolio_infobox_value_primary">
                        {I18N.floatval_unit(netProfit, 2, currencyTicker)}
                    </span>
                    <span className="ad_portfolio_infobox_value_secondary">
                        <ChangeArrow pct={netProfit / summary.settings.origin_bankroll} suffix="%" show_arrow={false} />
                    </span>
                </span>
            )
        }
    ]
}

const riskAdjustedValue = (summary && summary.totals && summary.totals.riskAdjustedReturnPercent) ? summary.totals.riskAdjustedReturnPercent : 0;
const betaScoreValue = (summary && summary.totals && summary.totals.betaScore) ? summary.totals.betaScore : 0;

let riskItems;
if (betaEnabled) {
	if (summary) {
        riskItems = [
            {
                label: <T>The risk-adjusted return of the portfolio</T>,
                value: (
                    <span className="ad_portfolio_infobox_compound">
                        <ChangeArrow pct={riskAdjustedValue} suffix="%" show_arrow={false} />
                    </span>
                ),
            },
            {
                label: <T>The risk score of the portfolio (Beta)</T>,
                value: (
                    <span>
                        <ChangeArrow pct={betaScoreValue} decimals={4} show_arrow={false} />
                    </span>
                )
            },
        ];
    } else {
        riskItems = [
            { label: <T>The risk-adjusted return of the portfolio</T>, value: (
                <span className="ad_portfolio_infobox_compound">
                    <ChangeArrow pct={0} suffix="%" show_arrow={false} />
                </span>
            ) },
            { label: <T>The risk score of the portfolio (Beta)</T>, value: (
                <span>
                    <ChangeArrow pct={0} decimals={4} show_arrow={false} />
                </span>
            ) },
        ];
    }
}


// TODO: Add diversification metrics when available
let diversificationItems = null;
if (diversificationEnabled && summary) {
	diversificationItems = [];
}

const placeHolderBox = <div className="ad_portfolio_infobox_placeholder"></div>

 return (
 	<Row className="ad_portfolio_summary_grid">
 		<PortfolioInfoBox title={<T>Overview</T>} items={overviewItems} />
		{ riskItems ? <PortfolioInfoBox title={<T>Risk</T>} items={riskItems} /> : placeHolderBox }
		{ diversificationItems ? <PortfolioInfoBox title={<T>Diversification</T>} items={diversificationItems} /> : placeHolderBox }
 	</Row>
 );
};



// WEBPACK FOOTER //
// ./src/c/PortfolioInfoBox.js